"use client";

import { useEffect, useMemo } from "react";
import {
  QUERY_STATUS,
  type QueryOptions,
  type QueryResult,
} from "@/types/async-protocol";
import { useApi } from "@/contexts/api";
import { useQueryHook } from "@/lib/query/use-query-hook";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import type { SessionPathsResponse } from "@cabal/shared/domain/session/path/types.js";
import type {
  ProductDetailResponse,
  ProductSummary,
  ProductTimelineResponse,
} from "@cabal/shared/domain/session/product/types.js";
import type {
  VersionDetailResponse,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import {
  useSessionDataContext,
  type SessionDataContextValue,
} from "./context";
import {
  selectHydratedQueryState,
  selectProductDetail,
  selectProductDetailObservation,
  selectSessionDataQueryOptions,
  selectSessionVersions,
  selectSessionVersionsResult,
  selectSessionVersionsSignature,
  selectVersionDetail,
  selectVersionProductId,
  selectVersionReflectionObservation,
} from "./hook-selectors";
import { sessionDataKeys } from "./keys";

type SessionDataApi = ReturnType<typeof useApi>;

export function useSessionVersions(
  productId: string | null | undefined,
  limit = 50,
  options?: QueryOptions,
): QueryResult<VersionSummary[]> & {
  versions: VersionSummary[];
  isLoading: boolean;
} {
  const context = useSessionDataContext();
  const contextProduct = productId ? context.products[productId] : undefined;
  const contextVersionsById = context.versions;
  const versionSelection = useMemo(
    () =>
      selectSessionVersions({
        product: contextProduct,
        versionsById: contextVersionsById,
      }),
    [contextVersionsById, contextProduct],
  );
  const versionsSignature = selectSessionVersionsSignature({
    productId,
    product: contextProduct,
    productReads: context.productReads,
  });
  const queryOptions = selectSessionDataQueryOptions({
    isReady: Boolean(productId),
    options,
  });
  const query = useQueryHook<VersionSummary[]>({
    queryKey: sessionDataKeys.versions(
      context.sessionId,
      productId ?? "none",
      limit,
      versionsSignature,
    ),
    queryFn: () => {
      if (!productId) return Promise.resolve([]);
      return context.actions.ensureProductVersions(productId, limit);
    },
    options: queryOptions,
  });
  const { versions, queryState } = selectSessionVersionsResult({
    queryData: query.data,
    queryStatus: query.status,
    queryError: query.error,
    versionSelection,
  });

  return {
    ...query,
    data: versions,
    versions,
    ...queryState,
  };
}

export function useSessionProductDetail(
  productId: string | null | undefined,
  options?: QueryOptions,
): QueryResult<ProductDetailResponse> & { isLoading: boolean } {
  const api = useApi();
  const context = useSessionDataContext();
  const query = useSessionProductDetailQuery({
    api,
    context,
    productId,
    options,
  });
  const { fetchedProductDetail, detailData, hasProductDetail } =
    selectProductDetail({
      productId,
      cachedDetailsById: context.productDetailsById,
      fetchedDetail: query.data,
    });
  const queryState = selectHydratedQueryState({
    hasData: hasProductDetail,
    queryStatus: query.status,
    queryError: query.error,
  });

  useCacheProductDetail({
    cacheProductDetail: context.actions.cacheProductDetail,
    fetchedProductDetail,
  });
  useProductDetailObservation({
    detailData,
    markProductDetailObserved: context.actions.markProductDetailObserved,
  });

  return {
    ...query,
    data: detailData,
    ...queryState,
  };
}

export function useSessionProductTimeline(
  productId: string | null | undefined,
  options?: QueryOptions,
): QueryResult<ProductTimelineResponse> & { isLoading: boolean } {
  const api = useApi();
  const context = useSessionDataContext();
  const queryOptions = selectSessionDataQueryOptions({
    isReady: Boolean(productId),
    options,
  });

  const query = useQueryHook<ProductTimelineResponse>({
    queryKey: sessionDataKeys.productTimeline(
      context.sessionId,
      productId ?? "none",
    ),
    queryFn: () =>
      fetchProductTimeline({
        api,
        sessionId: context.sessionId,
        productId,
      }),
    options: queryOptions,
  });

  return {
    ...query,
    isLoading: query.status === QUERY_STATUS.Loading,
  };
}

export function useSessionPaths(
  options?: QueryOptions,
): QueryResult<SessionPathsResponse> & { isLoading: boolean } {
  const api = useApi();
  const context = useSessionDataContext();
  const queryOptions = selectSessionDataQueryOptions({
    isReady: true,
    options,
  });

  const query = useQueryHook<SessionPathsResponse>({
    queryKey: sessionDataKeys.paths(context.sessionId),
    queryFn: () => api.sessions.byId(context.sessionId).paths(),
    options: queryOptions,
  });

  return {
    ...query,
    isLoading: query.status === QUERY_STATUS.Loading,
  };
}

export function useSessionVersion(
  versionId: string | null | undefined,
  options?: QueryOptions,
): QueryResult<VersionDetailResponse> & { isLoading: boolean } {
  const api = useApi();
  const context = useSessionDataContext();
  const cachedVersionDetail = selectVersionDetail({
    versionId,
    cachedDetailsById: context.versionDetailsById,
    fetchedDetail: null,
  }).detailData;
  const productId = useVersionProductId({
    versionId,
    productsById: context.products,
    cachedVersionDetail,
  });
  const query = useSessionVersionDetailQuery({
    api,
    context,
    versionId,
    productId,
    options,
  });
  const { fetchedVersionDetail, detailData, hasVersionDetail } =
    selectVersionDetail({
      versionId,
      cachedDetailsById: context.versionDetailsById,
      fetchedDetail: query.data,
    });
  const queryState = selectHydratedQueryState({
    hasData: hasVersionDetail,
    queryStatus: query.status,
    queryError: query.error,
  });

  useCacheVersionDetail({
    cacheVersionDetail: context.actions.cacheVersionDetail,
    fetchedVersionDetail,
  });
  useVersionReflectionObservation({
    detailData,
    markReflectionsObserved: context.actions.markReflectionsObserved,
  });

  return {
    ...query,
    data: detailData,
    ...queryState,
  };
}

export function useSessionCollabs(
  productId: string | null | undefined,
  options?: QueryOptions,
): QueryResult<Collab[]> & { collabs: Collab[]; isLoading: boolean } {
  const context = useSessionDataContext();
  const queryOptions = selectSessionDataQueryOptions({
    isReady: true,
    options,
  });
  const query = useQueryHook<Collab[]>({
    queryKey: sessionDataKeys.collabs(context.sessionId, productId ?? "main"),
    queryFn: () =>
      fetchSessionCollabs({
        context,
        productId,
      }),
    options: queryOptions,
  });
  const collabs = query.data ?? [];

  return {
    ...query,
    data: collabs,
    collabs,
    isLoading: query.status === QUERY_STATUS.Loading,
  };
}

function useSessionProductDetailQuery(input: {
  api: SessionDataApi;
  context: SessionDataContextValue;
  productId: string | null | undefined;
  options: QueryOptions | undefined;
}): QueryResult<ProductDetailResponse> {
  const { api, context, productId, options } = input;
  const queryOptions = selectSessionDataQueryOptions({
    isReady: Boolean(productId),
    options,
  });

  return useQueryHook<ProductDetailResponse>({
    queryKey: sessionDataKeys.productDetail(
      context.sessionId,
      productId ?? "none",
    ),
    queryFn: () => {
      if (!productId) throw new Error("productId is required.");
      return api.sessions
        .byId(context.sessionId)
        .products.byId(productId)
        .get();
    },
    options: queryOptions,
  });
}

function useCacheProductDetail(input: {
  cacheProductDetail: SessionDataContextValue["actions"]["cacheProductDetail"];
  fetchedProductDetail: ProductDetailResponse | null;
}): void {
  const { cacheProductDetail, fetchedProductDetail } = input;

  useEffect(() => {
    if (!fetchedProductDetail) return;
    cacheProductDetail({ detail: fetchedProductDetail });
  }, [cacheProductDetail, fetchedProductDetail]);
}

function useProductDetailObservation(input: {
  detailData: ProductDetailResponse | null;
  markProductDetailObserved: SessionDataContextValue["actions"]["markProductDetailObserved"];
}): void {
  const { detailData, markProductDetailObserved } = input;
  const productDetailObservation = useMemo(
    () => selectProductDetailObservation({ detail: detailData }),
    [detailData],
  );

  useEffect(() => {
    if (!productDetailObservation) return;
    markProductDetailObserved(productDetailObservation);
  }, [productDetailObservation, markProductDetailObserved]);
}

function useVersionProductId(input: {
  versionId: string | null | undefined;
  productsById: Record<string, ProductSummary>;
  cachedVersionDetail: VersionDetailResponse | null;
}): string | null {
  const { versionId, productsById, cachedVersionDetail } = input;

  return useMemo(
    () =>
      selectVersionProductId({
        versionId,
        productsById,
        cachedVersionDetail,
      }),
    [versionId, productsById, cachedVersionDetail],
  );
}

function useSessionVersionDetailQuery(input: {
  api: SessionDataApi;
  context: SessionDataContextValue;
  versionId: string | null | undefined;
  productId: string | null;
  options: QueryOptions | undefined;
}): QueryResult<VersionDetailResponse> {
  const { api, context, versionId, productId, options } = input;
  const queryOptions = selectSessionDataQueryOptions({
    isReady: Boolean(versionId && productId),
    options,
  });

  return useQueryHook<VersionDetailResponse>({
    queryKey: sessionDataKeys.versionDetail(
      context.sessionId,
      versionId ?? "none",
    ),
    queryFn: () => {
      if (!versionId || !productId) {
        throw new Error("versionId and productId are required.");
      }

      return api.sessions
        .byId(context.sessionId)
        .products.byId(productId)
        .versionById(versionId)();
    },
    options: queryOptions,
  });
}

function useCacheVersionDetail(input: {
  cacheVersionDetail: SessionDataContextValue["actions"]["cacheVersionDetail"];
  fetchedVersionDetail: VersionDetailResponse | null;
}): void {
  const { cacheVersionDetail, fetchedVersionDetail } = input;

  useEffect(() => {
    if (!fetchedVersionDetail) return;
    cacheVersionDetail({ detail: fetchedVersionDetail });
  }, [cacheVersionDetail, fetchedVersionDetail]);
}

function useVersionReflectionObservation(input: {
  detailData: VersionDetailResponse | null;
  markReflectionsObserved: SessionDataContextValue["actions"]["markReflectionsObserved"];
}): void {
  const { detailData, markReflectionsObserved } = input;
  const reflectionObservation = useMemo(
    () => selectVersionReflectionObservation({ detail: detailData }),
    [detailData],
  );

  useEffect(() => {
    if (!reflectionObservation) return;
    markReflectionsObserved(reflectionObservation);
  }, [reflectionObservation, markReflectionsObserved]);
}

function fetchProductTimeline(input: {
  api: SessionDataApi;
  sessionId: string;
  productId: string | null | undefined;
}): Promise<ProductTimelineResponse> {
  if (!input.productId) throw new Error("productId is required.");

  return input.api.sessions
    .byId(input.sessionId)
    .products.byId(input.productId)
    .timeline();
}

function fetchSessionCollabs(input: {
  context: SessionDataContextValue;
  productId: string | null | undefined;
}): Promise<Collab[]> {
  if (!input.productId) {
    return Promise.resolve(selectMainChatCollabs({ context: input.context }));
  }

  return input.context.actions.ensureProductCollabs(input.productId);
}

function selectMainChatCollabs(input: {
  context: SessionDataContextValue;
}): Collab[] {
  return input.context.chatMessageIds
    .map((messageId) => input.context.chatMessages[messageId])
    .filter((collab): collab is Collab => Boolean(collab));
}
